import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'

// evertel
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Row, Col, Button
} from '@evertel/web/ui'
import { SchemaBuilderController } from '../../../controller'
import { parseSchemaRows } from '../../schema-builder-dnd/types'
import { RoomTileFieldsCard } from './RoomTileFieldsCard'
import { RoomTilePreviewCard } from './RoomTilePreviewCard'

const TILE_FIELD_TYPES = ['text', 'dropdown', 'checkbox', 'checkbox-group', 'date', 'map', 'user']

interface RoomTileSettingsModalProps {
    visible: boolean
    onClose: () => void
    schemaBuilderController: SchemaBuilderController
}

const RoomTileSettingsModal: React.FC<RoomTileSettingsModalProps> = observer(({
    visible,
    onClose,
    schemaBuilderController
}) => {

    const [orderedIds, setOrderedIds] = useState<string[]>([])
    const [enabledIds, setEnabledIds] = useState<string[]>([])
    const [removedIds, setRemovedIds] = useState<string[]>([])

    // only root-level items are offered: a tile stays a flat list, so subform fields are out.
    // The id also has to be a real schema.fields entry, because that is what the compiled tile
    // resolves a saved id against - legacy inline items carry a synthetic id that never would
    const schemaFields = schemaBuilderController.schema?.fields || {}

    const fields = parseSchemaRows(schemaBuilderController.schema)
        .flatMap(row => row.items)
        .filter(item => TILE_FIELD_TYPES.includes(item.type) && item.id !== 'title' && !!schemaFields[item.id])

    // saved entries whose field the form has lost. They have no toggle - being in the tile at
    // all means they were on - and removing one is the only thing left to do with it
    const allDanglingFields = schemaBuilderController.danglingTileFields
    const danglingFields = allDanglingFields.filter(removed => !removedIds.includes(removed.id))
    const danglingIds = danglingFields.map(removed => removed.id)

    // the state the table starts from: whatever the template currently holds. Reads the full
    // dangling list rather than the visible one, because it is also what puts back an entry
    // that was removed and then discarded
    const loadFromTemplate = () => {
        const savedIds = schemaBuilderController.tileSchema?.fields || []
        const fieldIds = fields.map(field => field.id)
        const allDanglingIds = allDanglingFields.map(removed => removed.id)

        // saved entries keep the order they were saved in, and the fields that were never
        // turned on follow in form order
        const knownIds = savedIds.filter(id => fieldIds.includes(id) || allDanglingIds.includes(id))

        setOrderedIds([...knownIds, ...fieldIds.filter(id => !knownIds.includes(id))])
        setEnabledIds(knownIds.filter(id => fieldIds.includes(id)))
        setRemovedIds([])
    }

    const restoreDefaults = () => {
        // every field off, in the order the form has them. Entries for removed fields are not
        // selections the user can remake, so they survive and stay at the top
        setOrderedIds([...orderedIds.filter(id => danglingIds.includes(id)), ...fields.map(field => field.id)])
        setEnabledIds([])
    }

    useEffect(() => {
        if (!visible) return

        loadFromTemplate()
    }, [visible])

    const onToggleField = (fieldId: string, checked: boolean) => {
        setEnabledIds(prev => (checked) ? [...prev, fieldId] : prev.filter(id => id !== fieldId))
    }

    const onRemoveField = (fieldId: string) => {
        setRemovedIds(prev => [...prev, fieldId])
        setOrderedIds(prev => prev.filter(id => id !== fieldId))
    }

    // what the tile will show: the enabled fields plus the surviving removed ones, in the order
    // the table has them
    const selectedIds = orderedIds.filter(fieldId => enabledIds.includes(fieldId) || danglingIds.includes(fieldId))

    const selectedFields = selectedIds
        .map(fieldId => fields.find(field => field.id === fieldId))
        .filter(Boolean)

    const onDone = () => {
        schemaBuilderController.setTileSchema(selectedIds)
        onClose()
    }

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            size="xl">
            <ModalHeader
                title="Room preview settings"
                subtitle="Choose which fields appear in the tile when this EverDoc is shared in a room. Applies to future shares and updates."
            />
            <ModalBody className="room-tile-settings-modal-body">
                <Row className="h-100">
                    <Col
                        xs={7}
                        className="h-100 pe-2">
                        <RoomTileFieldsCard
                            fields={fields}
                            danglingFields={danglingFields}
                            orderedIds={orderedIds}
                            enabledIds={enabledIds}
                            onReorder={setOrderedIds}
                            onToggleField={onToggleField}
                            onRemoveField={onRemoveField}
                            onRestoreDefaults={restoreDefaults}
                        />
                    </Col>
                    <Col
                        xs={5}
                        className="h-100 ps-2">
                        <RoomTilePreviewCard
                            fields={selectedFields}
                            schemaBuilderController={schemaBuilderController}
                        />
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter align="right">
                <Button
                    color="neutral"
                    className="me-2"
                    onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={onDone}>
                    Done
                </Button>
            </ModalFooter>
        </Modal>
    )
})

export { RoomTileSettingsModal }
